package dev.lingvo.site.components

import androidx.compose.runtime.Stable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.navigation.Router
import dev.lingvo.shared.MtJobDto
import dev.lingvo.shared.MtJobState
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.components.automation.showJobToast
import dev.lingvo.site.components.automation.showPretranslateStarted
import dev.lingvo.site.ds.LToastVariant
import dev.lingvo.site.ds.ToastState
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.launch

/**
 * How a finished run reads in a toast.
 *
 * Counts, never "done": the number a receipt states is the number of strings that were actually
 * written, which is not always the number the estimate priced — a provider refusal writes nothing
 * for that string and the run still finishes. `tr`/`tPlural` rather than `t`, because every caller
 * reaches this from a coroutine rather than from composition.
 */
internal fun readinessJobReceipt(job: MtJobDto): String = when (job.state) {
    MtJobState.FAILED -> trf(
        "readiness_run_store.pre_translate_stopped",
        "Pre-translate stopped: {reason}",
        "reason" to (
            job.error?.takeIf(String::isNotBlank)
                ?: tr("readiness_run_store.no_reason_given", "no reason given")
            ),
    )

    MtJobState.DONE -> {
        val refused = job.failed + job.rejected
        if (refused > 0) {
            trf(
                "readiness_run_store.pre_translated_with_refusals",
                "Pre-translated {done}, {refused} not written",
                "done" to job.done.toString(),
                "refused" to refused.toString(),
            )
        } else {
            tPlural(
                "readiness_run_store.count.pre_translated",
                job.done,
                "Pre-translated {count} string",
                "Pre-translated {count} strings",
            )
        }
    }

    // A flow that ends on a RUNNING job is a poll that stopped answering rather than a run that
    // stopped: the receipt page is the only thing that can still say how it ended.
    MtJobState.RUNNING -> tr(
        "readiness_run_store.this_run_is_still_going",
        "This run is still going.",
    )
}

private fun readinessRunFailure(failure: Throwable): String = failure.message?.takeIf(String::isNotBlank)
    ?: tr(
        "readiness_run_store.lost_track_of_this_run_open",
        "Lost track of this run. Open the receipt to see how it ended.",
    )

/**
 * The pre-translate runs a readiness band is watching, one language at a time.
 *
 * **The band starts runs, so the band has to be able to show them.** A row that opens an estimate,
 * spends the workspace's credits and then goes back to looking exactly as it did leaves the reader
 * with a number that is quietly wrong and nothing saying why — so the row that pressed the button
 * carries the progress until the run ends, and the toast carries the receipt afterwards.
 *
 * **A duplicate press attaches rather than refuses.** `POST /mt/pretranslate` answers
 * `409 job_already_running` with the run already going in `details.jobId`, and
 * [dev.lingvo.site.components.automation.PretranslateConfirm] reports that id through `onStarted`
 * exactly as it reports a fresh one. [attach] therefore cannot tell the two apart, and must not: the
 * work the reader asked for is happening either way, and the honest answer is its progress.
 *
 * **Polling stops by itself.** `ApiClient.pollJob` completes on the first non-`RUNNING` answer, and
 * everything here runs in the scope the band remembers, so leaving the page — or collapsing the
 * disclosure that drew the row — cancels the request in flight rather than leaving a timer running
 * against a page nobody is looking at.
 *
 * @param poll seam for tests; production is `ApiClient.pollJob`.
 */
@Stable
internal class ReadinessRunStore(
    private val projectSlug: String,
    private val scope: CoroutineScope,
    private val toasts: ToastState?,
    private val router: Router?,
    private val poll: (projectSlug: String, jobId: String) -> Flow<MtJobDto> =
        { slug, jobId -> ApiClient.pollJob(slug, jobId) },
) {
    /** The latest answer for each language that has a run, keyed by locale. */
    var runs by mutableStateOf<Map<String, MtJobDto>>(emptyMap())
        private set

    /** Languages whose run has an id but has not been described by the server yet. */
    var attaching by mutableStateOf<Set<String>>(emptySet())
        private set

    private val polls = mutableMapOf<String, Job>()

    fun runFor(locale: String): MtJobDto? = runs[locale]

    /** Whether this language has a run worth drawing a readout for. */
    fun isRunning(locale: String): Boolean =
        locale in attaching || runs[locale]?.state == MtJobState.RUNNING

    /**
     * Follows one run to its end: progress while it goes, a receipt when it stops.
     *
     * The "started" toast is shown from the *first server answer* rather than from the press, so its
     * count is the run's real size — the estimate is a price, and a run attached through a 409 was
     * sized by somebody else entirely. It is dismissed when the receipt replaces it, so one run
     * never leaves two messages on screen.
     */
    fun attach(locale: String, jobId: String) {
        polls.remove(locale)?.cancel()
        attaching = attaching + locale
        polls[locale] = scope.launch {
            var startedToast: Int? = null
            try {
                poll(projectSlug, jobId).collect { latest ->
                    runs = runs + (locale to latest)
                    attaching = attaching - locale
                    if (startedToast == null && latest.state == MtJobState.RUNNING) {
                        startedToast = announceStart(jobId, latest.total)
                    }
                }
            } catch (cancelled: CancellationException) {
                // Leaving the page, or the band that drew this row. Neither is a message.
                throw cancelled
            } catch (failure: Throwable) {
                attaching = attaching - locale
                toasts?.show(readinessRunFailure(failure), LToastVariant.DANGER)
                return@launch
            }
            val settled = runs[locale] ?: return@launch
            startedToast?.let { toasts?.dismiss(it) }
            announceReceipt(settled)
        }
    }

    /** Stops every poll this store owns. The band calls it when it leaves composition. */
    fun dispose() {
        polls.values.forEach { it.cancel() }
        polls.clear()
    }

    private fun announceStart(jobId: String, strings: Int): Int? {
        val channel = toasts ?: return null
        val target = router ?: return null
        return showPretranslateStarted(channel, target, projectSlug, jobId, strings)
    }

    private fun announceReceipt(job: MtJobDto) {
        val channel = toasts ?: return
        val target = router ?: return
        showJobToast(
            toasts = channel,
            router = target,
            projectSlug = projectSlug,
            jobId = job.id,
            message = readinessJobReceipt(job),
            variant = if (job.state == MtJobState.FAILED) LToastVariant.DANGER else LToastVariant.SUCCESS,
        )
    }
}
